import type * as React from 'react'

import { useIsSplitEnabled } from '@buffer-mono/features'
import {
  Dialog,
  Text,
  Button,
  IconButton,
  CopyIcon,
  Flex,
  Link,
  Tag,
  BufferIcon,
  useControllableState,
  PencilIcon,
  toast,
} from '@buffer-mono/popcorn'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import { useIdeaComposer } from '~publish/components/IdeaManagementRouter'
import { NewPostComposerTrigger } from '~publish/components/NewPostComposerTrigger'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import AppActionCreators from '~publish/legacy/composer/composer/action-creators/AppActionCreators'
import { createTemplates } from '~publish/legacy/routes'

import type { Template } from '../../notion-sync/interface'

import styles from './TemplateDialog.module.css'

type TemplateDialogProps = {
  template: Template
  cta: CTA | null
  preselectChannelId?: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
  children?: React.ReactNode
}

export const TemplateDialog = ({
  template,
  cta,
  preselectChannelId,
  open: openProp,
  onOpenChange,
  children,
}: TemplateDialogProps): JSX.Element | null => {
  const enableNewUX = useIsSplitEnabled('template-card-ux-refresh')
  const [open, setOpen] = useControllableState({
    prop: openProp,
    defaultProp: false,
    onChange: onOpenChange,
  })
  const { createIdeaWithComposer } = useIdeaComposer()

  const organizationId = useOrganizationId()

  if (!template) {
    return null
  }

  const handleOpenChange = (open: boolean): void => {
    if (!open) {
      // Attempt to refocus the template trigger after the dialog is closed
      requestAnimationFrame(() => {
        const templateTrigger = document.getElementById(
          `template-card-${template.id}`,
        )
        if (templateTrigger) {
          templateTrigger.focus()
        }
      })
    }
    setOpen(open)
  }

  const handleSaveAsIdea = (): void => {
    BufferTracker.templateUsed({
      cta: cta ?? 'create-templates-dialog-newIdea-1',
      organizationId: organizationId ?? '',
      product: 'publish',
      templateId: template.id,
      templateUse: 'createIdea',
    })
    // We provide basePath to ensure we navigate to the correct path
    // instead of simply appending the idea composer path to the current path
    createIdeaWithComposer({
      source: cta ?? 'create-templates-dialog-newIdea-1',
      title: template.title,
      text: template.body,
      templateId: template.id,
      basePath: createTemplates.route,
    })
  }

  const handleCopyTemplate = (): void => {
    navigator.clipboard
      .writeText(template.body)
      .then(() => {
        return toast.success('Template copied to clipboard')
      })
      .catch(() => {
        return toast.error('Failed to copy template to clipboard')
      })
  }

  const handleCreatePost = (): void => {
    // <NewPostComposerTrigger> triggers opening the new post composer
    // so we just need to close the dialog and track the action here
    BufferTracker.templateUsed({
      cta: cta ?? 'create-templates-dialog-newPost-1',
      organizationId: organizationId ?? '',
      product: 'publish',
      templateId: template.id,
      templateUse: 'createPost',
    })

    if (enableNewUX) {
      // Temporary hack to ensure the omnibox is enabled
      // when the new post composer is opened
      // Since it is not immediately enabled, we need to wait for it to be enabled
      const start = Date.now()
      const interval = setInterval(() => {
        AppActionCreators.updateOmniboxState(true)
        if (Date.now() - start > 2000) {
          clearInterval(interval)
        }
      }, 200)
    }

    handleOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Dialog.Trigger>{children}</Dialog.Trigger>
      <Dialog.Content className={styles.content}>
        <Dialog.Header className={styles.section}>
          <Dialog.Title size="large" className={styles.title}>
            <div
              className={styles.templateEmoji}
              style={{ backgroundColor: `rgba(${template.color}, 0.05)` }}
              aria-hidden={true}
            >
              {template.emoji}
            </div>
            {template.title}
          </Dialog.Title>
          <Dialog.Description>
            <Text size="lg" color="subtle">
              {template.description}
            </Text>
          </Dialog.Description>
        </Dialog.Header>
        <Dialog.Body className={styles.section}>
          {!enableNewUX && (
            <Flex direction="column" gap="lg">
              <Text size="lg" className={styles.templateTextOld}>
                {template.body}
              </Text>
            </Flex>
          )}
          {enableNewUX && (
            <Flex direction="column" gap="lg" className={styles.templateText}>
              <Text size="lg">{template.body}</Text>
              <Flex gap="sm" justify="end" fullWidth>
                <IconButton
                  variant="secondary"
                  size="medium"
                  onClick={handleCopyTemplate}
                  label="Use Template"
                >
                  <CopyIcon />
                </IconButton>
                <NewPostComposerTrigger
                  channels={
                    preselectChannelId ? [preselectChannelId] : undefined
                  }
                  prefillPostData={{ text: template.body }}
                  cta="create-templates-dialog-newPost-1"
                  templateId={template.id}
                >
                  <Button
                    variant="primary"
                    size="medium"
                    onClick={handleCreatePost}
                  >
                    <PencilIcon />
                    Use Template
                  </Button>
                </NewPostComposerTrigger>
              </Flex>
            </Flex>
          )}
          <Flex direction="row" gap="2xs" className={styles.templateMeta}>
            <BufferIcon
              style={{
                verticalAlign: 'middle',
                transform: 'translateY(2px)',
              }}
            />
            <Text color="subtle">
              By&nbsp;
              <Link
                external
                href="https://buffer.com"
                style={{ color: 'var(--color-text-subtle)' }}
              >
                Buffer
              </Link>
            </Text>
            <Text size="lg" color="subtle">
              &nbsp;•&nbsp;
            </Text>
            <Flex gap="xs" wrap="wrap">
              <Tag className={styles.templateTag}>{template.goal}</Tag>
              <Tag className={styles.templateTag}>{template.type}</Tag>
            </Flex>
          </Flex>
        </Dialog.Body>
        {!enableNewUX && (
          <Dialog.Footer className={styles.section}>
            <Flex gap="sm" justify="end">
              <Button
                variant="secondary"
                size="large"
                onClick={handleSaveAsIdea}
              >
                Save as Idea
              </Button>
              <NewPostComposerTrigger
                channels={preselectChannelId ? [preselectChannelId] : undefined}
                prefillPostData={{ text: template.body }}
                cta="create-templates-dialog-newPost-1"
                templateId={template.id}
              >
                <Button
                  variant="primary"
                  size="large"
                  onClick={handleCreatePost}
                >
                  Create Post
                </Button>
              </NewPostComposerTrigger>
            </Flex>
          </Dialog.Footer>
        )}
        <Dialog.CloseButton />
      </Dialog.Content>
    </Dialog>
  )
}
